/**
 * CORE-1900 — «AgoraRTCError WS_ABORT: type: publish» as an unhandled rejection.
 *
 * GlitchTip FRONTEND-25/26, dev 28.10 12:37–12:44 (QA room for CORE-1859): the
 * Agora signalling socket dropped while a publish was in flight, the SDK
 * rejected the request with `WS_ABORT` (it rejects every pending signal request
 * on `WS_CLOSED` / `WS_RECONNECTING`), and the error reached Sentry through
 * `onunhandledrejection`, with `reconcilePublications` in its async stack.
 *
 * Our own `await client.publish(...)` already sits in a try/catch. The
 * unhandled copy is the SDK's: agora-rtc-sdk-ng 4.24 does
 * `catch (e) { throw gen.throw(e), e }` around the gateway publish, so the
 * async generator's `throw()` promise rejects with the same error and nobody
 * holds it. We cannot wrap that promise; we can only (a) treat the abort we DO
 * catch as the expected event it is, and (b) keep its orphaned twin out of
 * Sentry, where it is a duplicate of a row telemetry already has
 * (`rtc_js_unhandled_rejection`, plus the classified row below).
 *
 * Pure — no SDK import (INVARIANTS I-4), so it is safe on every platform.
 */

/** Codes the SDK uses for «the operation was cut off», not «it failed». */
const ABORT_CODES = new Set(['WS_ABORT', 'OPERATION_ABORTED']);

/**
 * The Agora error code of `err`: the `code` field of an `AgoraRTCError`, or the
 * code embedded in its message (`AgoraRTCError WS_ABORT: type: publish`) when
 * the object has been flattened on the way. `null` for anything else.
 */
export function agoraErrorCode(err: unknown): string | null {
  if (!err || typeof err !== 'object') {
    if (typeof err === 'string') return codeFromMessage(err);
    return null;
  }
  const code = (err as { code?: unknown }).code;
  if (typeof code === 'string' && /^[A-Z][A-Z_]+$/.test(code)) return code;
  const message = (err as { message?: unknown }).message;
  return typeof message === 'string' ? codeFromMessage(message) : null;
}

function codeFromMessage(message: string): string | null {
  const m = /AgoraRTC(?:Error|Exception)\s+([A-Z][A-Z_]+)/.exec(message);
  return m ? m[1] : null;
}

/**
 * Is this publish / unpublish / subscribe failure the expected side effect of a
 * reconnect or a leave, rather than a real failure?
 *
 *  - `WS_ABORT` / `OPERATION_ABORTED` — always: the SDK raises them only when
 *    the socket the request travelled on went away. The repair sweeps
 *    (`reconcilePublications` on `RECONNECTING → CONNECTED` and on the
 *    20-second beat; `reconcileRemotes` likewise) put the publication or the
 *    subscription back once the channel is live again.
 *  - `INVALID_OPERATION` — only when the connection is not live at the moment of
 *    the failure (the SDK's «can not publish/subscribe when connection state is
 *    …»). On a CONNECTED client it means we called the SDK wrong, and that must
 *    stay a real failure.
 */
export function isExpectedAgoraAbort(err: unknown, connectionState: string): boolean {
  const code = agoraErrorCode(err);
  if (!code) return false;
  if (ABORT_CODES.has(code)) return true;
  if (code === 'INVALID_OPERATION') return connectionState !== 'CONNECTED';
  return false;
}

export type RepublishOutcome =
  | { media: 'audio' | 'video'; status: 'restored' }
  | { media: 'audio' | 'video'; status: 'deferred'; code: string | null; connectionState: string }
  | { media: 'audio' | 'video'; status: 'failed'; error: unknown };

/**
 * The publish loop of `reconcilePublications`, without the hook around it.
 *
 * Never rejects: every `publish` is awaited inside a try/catch and its outcome
 * goes to `report`. An expected abort ({@link isExpectedAgoraAbort}) is
 * `deferred` and ends the pass — the socket is gone, so the next track would
 * abort the same way, and the reconnect sweep retries both once the channel is
 * back. Anything else is `failed` and the pass moves on to the next track, as
 * before.
 *
 * @returns `false` when the room switched under us (`isCurrent()` turned
 *   false) — the caller must stop touching the old client.
 */
export async function republishMissing(input: {
  missing: readonly ('audio' | 'video')[];
  trackFor: (media: 'audio' | 'video') => unknown;
  publish: (track: unknown) => Promise<unknown>;
  isCurrent: () => boolean;
  connectionState: () => string;
  report: (outcome: RepublishOutcome) => void;
}): Promise<boolean> {
  for (const media of input.missing) {
    const track = input.trackFor(media);
    if (!track) continue;
    try {
      await input.publish(track);
    } catch (err) {
      if (!input.isCurrent()) return false;
      const connectionState = input.connectionState();
      if (isExpectedAgoraAbort(err, connectionState)) {
        input.report({ media, status: 'deferred', code: agoraErrorCode(err), connectionState });
        return true;
      }
      input.report({ media, status: 'failed', error: err });
      continue;
    }
    if (!input.isCurrent()) return false;
    input.report({ media, status: 'restored' });
  }
  return true;
}

/**
 * CORE-1900 (QA FAIL 03.11.2026, dev 8bd1b45, room 6806) — codes whose
 * UNHANDLED copy can only be the SDK's own.
 *
 * QA cut the Agora signalling socket right after the camera's `publish` frame.
 * agora-rtc-sdk-ng 4.24.2 then rejects two promises nobody holds — the
 * `r.throw(e)` of `P2PChannel.doPublish` and the `i.throw(e)` of
 * `_publishHighStream` — while `client.publish` itself RESOLVES (`WS_ABORT` is
 * swallowed there) and the track goes to the SDK's `pendingLocalTracks`. On the
 * gateway reconnect the SDK republishes that list itself, fire-and-forget
 * (`this._p2pChannel.republish()`); while the peer connection is not back yet
 * its own `throwIfTrackTypeNotMatch` refuses the very video track already in
 * the list with `CAN_NOT_PUBLISH_MULTIPLE_VIDEO_TRACKS`, unhandled again. Every
 * publish of ours is awaited inside a try/catch, so an unhandled rejection
 * with one of these codes is the SDK's orphan by construction. Restoring the
 * publication is the repair sweep's job (`reconcilePublications` on reconnect
 * and on the 20-second beat), which checks `client.localTracks`, not promises.
 */
const SDK_ORPHAN_CODES = new Set(['WS_ABORT', 'OPERATION_ABORTED', 'CAN_NOT_PUBLISH_MULTIPLE_VIDEO_TRACKS']);

function looksLikeAgoraError(reason: unknown): boolean {
  if (typeof reason === 'string') return /AgoraRTC(?:Error|Exception)/.test(reason);
  if (!reason || typeof reason !== 'object') return false;
  const { name, message } = reason as { name?: unknown; message?: unknown };
  return (typeof name === 'string' && /^AgoraRTC/.test(name))
    || (typeof message === 'string' && /AgoraRTC(?:Error|Exception)/.test(message));
}

/** Is this unhandled-rejection reason one of the SDK's orphans (see {@link SDK_ORPHAN_CODES})? */
export function isSdkOrphanAgoraRejection(reason: unknown): boolean {
  if (!looksLikeAgoraError(reason)) return false;
  const code = agoraErrorCode(reason);
  return code !== null && SDK_ORPHAN_CODES.has(code);
}

/**
 * `unhandledrejection` step: marks the SDK's orphan as handled
 * (`preventDefault()` — the browser no longer reports it) and says so. The
 * row in RTC telemetry (`rtc_js_unhandled_rejection`) stays, with `absorbed`.
 */
export function absorbSdkOrphanRejection(event: { reason?: unknown; preventDefault?: () => void }): boolean {
  if (!isSdkOrphanAgoraRejection(event?.reason)) return false;
  if (typeof event.preventDefault === 'function') event.preventDefault();
  return true;
}

interface SentryExceptionLike {
  type?: string;
  value?: string;
  mechanism?: { type?: string; handled?: boolean };
}

/**
 * Should Sentry drop this event? Only the SDK's orphan: an UNHANDLED rejection
 * (`onunhandledrejection`, or `handled: false`) whose error is an Agora
 * `WS_ABORT` / `OPERATION_ABORTED`, or (QA FAIL 03.11) the
 * `CAN_NOT_PUBLISH_MULTIPLE_VIDEO_TRACKS` of the SDK's own republish. A handled
 * capture, an Agora error of any other code (`INVALID_OPERATION` included) and
 * every non-Agora error pass through untouched.
 */
export function isOrphanedAgoraAbortEvent(event: unknown): boolean {
  const values = (event as { exception?: { values?: SentryExceptionLike[] } } | null)?.exception?.values;
  if (!Array.isArray(values) || values.length === 0) return false;
  return values.some((ex) => {
    const unhandled = ex?.mechanism?.type === 'onunhandledrejection' || ex?.mechanism?.handled === false;
    if (!unhandled) return false;
    const code = codeFromMessage(`${ex?.type ?? ''} ${ex?.value ?? ''}`)
      ?? (/^AgoraRTC/.test(ex?.type ?? '') ? codeFromMessage(`AgoraRTCError ${ex?.value ?? ''}`) : null);
    return code !== null && SDK_ORPHAN_CODES.has(code);
  });
}

/** `beforeSend` step: `null` for {@link isOrphanedAgoraAbortEvent}, else the event. */
export function dropOrphanedAgoraAbort<T>(event: T): T | null {
  if (event === null) return null;
  return isOrphanedAgoraAbortEvent(event) ? null : event;
}
